import { Plus, X } from "lucide-react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { Fertilizer } from "../../../../../types/prodcut-data-review/product-data.types";

interface FertilizerRowFormValues {
  id: string;
  fertilizerQuantity: string;
}

interface FertilizerRowProps {
  fertilizer: Fertilizer;
  fertilizerList: any[];
  editing: boolean;
  lookupText: (list: any[], id: number | string) => string;
  onUpdate: (id: number | string, patch: Partial<Fertilizer>) => void;
  onRemove: (id: number | string) => void;
}

const FertilizerRow: React.FC<FertilizerRowProps> = ({
  fertilizer,
  fertilizerList,
  editing,
  lookupText,
  onUpdate,
  onRemove
}) => {
  const form = useForm<FertilizerRowFormValues>({
    defaultValues: { id: String(fertilizer.id), fertilizerQuantity: fertilizer.fertilizerQuantity }
  });

  useEffect(() => {
    const subscription = form.watch(data => {
      onUpdate(fertilizer.id, { id: Number(data.id), fertilizerQuantity: data.fertilizerQuantity });
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <Form {...form}>
      <TableRow>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormSelect
              control={form.control}
              name="id"
              label=""
              placeholder="Select fertilizer"
              options={fertilizerList}
            />
          ) : (
            <span className="tw-text-sm">{lookupText(fertilizerList, fertilizer.id) || "—"}</span>
          )}
        </TableCell>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormInputNumber control={form.control} name="fertilizerQuantity" label="" />
          ) : (
            <span className="tw-text-sm tw-font-mono">
              {fertilizer.fertilizerQuantity !== "" ? fertilizer.fertilizerQuantity : "—"}
            </span>
          )}
        </TableCell>
        <TableCell className="tw-text-right">
          {editing && (
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={() => onRemove(fertilizer.id)}
              title="Remove"
            >
              <X className="tw-h-3.5 tw-w-3.5" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    </Form>
  );
};

interface FertilizersSectionProps {
  fertilizers: Fertilizer[];
  fertilizerList: any[];
  editing: boolean;
  subSectionHeader: string;
  lookupText: (list: any[], id: number | string) => string;
  onAdd: () => void;
  onUpdate: (id: number | string, patch: Partial<Fertilizer>) => void;
  onRemove: (id: number | string) => void;
}

const FertilizersSection: React.FC<FertilizersSectionProps> = ({
  fertilizers,
  fertilizerList,
  editing,
  subSectionHeader,
  lookupText,
  onAdd,
  onUpdate,
  onRemove
}) => {
  return (
    <div className="tw-border-t">
      <div className={subSectionHeader}>
        <span className="tw-text-xs tw-font-semibold tw-text-primary tw-uppercase tw-tracking-wide">
          Fertilizers
        </span>
        {editing && (
          <Button variant="outline" size="sm" className="tw-h-7 tw-text-xs" onClick={onAdd}>
            <Plus className="tw-h-3 tw-w-3 tw-mr-1" />
            Add fertilizer
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw-text-xs">Fertilizer Name</TableHead>
            <TableHead className="tw-text-xs tw-w-[200px]">Amount (kg/ha)</TableHead>
            <TableHead className="tw-text-xs tw-w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fertilizers.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="tw-text-xs tw-text-muted-foreground tw-italic">
                No fertilizers.
              </TableCell>
            </TableRow>
          )}
          {fertilizers.map(f => (
            <FertilizerRow
              key={f.id}
              fertilizer={f}
              fertilizerList={fertilizerList}
              editing={editing}
              lookupText={lookupText}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default FertilizersSection;
